import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { AlertTriangle, CheckCircle2, Sparkles, UserMinus, UsersRound, X } from 'lucide-react';
import { buildAssigneeAuditTaskHref, buildAuditPipelineProcessInsights, buildAuditPipelineStageFocus } from './auditPipelineInsightUtils';
import { buildAuditManagementAnalytics, filterAuditManagementTasks } from './auditManagementAnalytics';
import './AuditPipelineInsights.css';

const EMPTY_SELECTION = {};
const DONUT_PALETTE = ['#1f7a83', '#3178a5', '#7b5db7', '#d97706', '#c2415d', '#16845b'];

/** One line, always visible, saying exactly what is currently narrowing every chart on this page. */
const ActiveFilterBanner = ({ selectedStage, portfolioFilter, onClearStage, onClearPortfolio }) => {
  if (!selectedStage && !portfolioFilter) return null;
  const label = selectedStage
    ? `Stage: ${selectedStage}`
    : `${portfolioFilter.type === 'process' ? 'Process' : portfolioFilter.type === 'risk' ? 'Signal' : portfolioFilter.type === 'standard' ? 'Standard' : 'Program'}: ${portfolioFilter.value}`;
  return (
    <div className="audit-pipeline-insights__active-filter" role="status">
      <Sparkles size={14} />
      <span>Filtering everything below by <strong>{label}</strong></span>
      <button
        type="button"
        onClick={selectedStage ? onClearStage : onClearPortfolio}
        aria-label="Clear filter"
      >
        Clear <X size={12} />
      </button>
    </div>
  );
};

const AuditPipelineInsights = ({
  tasks = [], schedules = [], stageCounts = {}, stageTotal = 0,
  selectedStage, onClearStage, loading = false, showManagementAnalytics = false,
  portfolioFilter = null, onPortfolioFilterChange,
  navigate,
}) => {
  const filteredTasks = useMemo(
    () => filterAuditManagementTasks(tasks, portfolioFilter),
    [tasks, portfolioFilter],
  );
  const insights = useMemo(
    () => buildAuditPipelineProcessInsights(filteredTasks, schedules, stageCounts),
    [filteredTasks, schedules, stageCounts],
  );
  const stageFocus = useMemo(
    () => buildAuditPipelineStageFocus(insights, selectedStage),
    [insights, selectedStage],
  );
  const [selected, setSelected] = useState(EMPTY_SELECTION);
  const [assigneeDialog, setAssigneeDialog] = useState(null);
  const managementAnalytics = useMemo(() => buildAuditManagementAnalytics(filteredTasks), [filteredTasks]);

  useEffect(() => {
    setSelected(EMPTY_SELECTION);
  }, [selectedStage]);

  const togglePortfolioFilter = (nextFilter) => {
    const resolved = portfolioFilter?.type === nextFilter.type && portfolioFilter?.value === nextFilter.value
      ? null
      : nextFilter;
    onPortfolioFilterChange?.(resolved);
    setSelected(EMPTY_SELECTION);
  };

  const selectStatus = (process, statusIndex) => {
    const status = process.statuses[statusIndex];
    if (!status) return;
    setSelected({ process: process.key, statusIndex, label: status.label, value: status.value });
    onPortfolioFilterChange?.({
      type: 'process',
      value: process.label,
      taskCodes: process.taskCodes,
    });
  };

  return (
    <div className="audit-pipeline-insights" aria-label="Audit pipeline process insights">
      <ActiveFilterBanner
        selectedStage={selectedStage}
        portfolioFilter={portfolioFilter}
        onClearStage={onClearStage}
        onClearPortfolio={() => onPortfolioFilterChange?.(null)}
      />
      <div className="audit-pipeline-insights__intro">
        <h4>Insight Audit Process workload</h4>
        {!loading && (
          <div className="audit-pipeline-insights__summary" aria-label="Stage distribution summary">
            <span>{stageTotal} projects in stage distribution</span>
            <span>{Number(stageCounts?.Completed) || 0} completed</span>
          </div>
        )}
      </div>

      {stageFocus && (
        <div className="audit-pipeline-insights__focus" role="status">
          <div className="audit-pipeline-insights__focus-heading">
            <Sparkles size={16} />
            <div>
              <span>Stage focus</span>
              <strong>{stageFocus.stage}</strong>
            </div>
          </div>
          <div className="audit-pipeline-insights__focus-metrics">
            <span><strong>{stageFocus.projectCount}</strong> projects</span>
            <span><strong>{stageFocus.workloadTotal}</strong> workload records</span>
            <span><strong>{stageFocus.ownerCount}</strong> owners</span>
            <span>
              <strong>{stageFocus.bottleneck ? `${stageFocus.bottleneck.value} ${stageFocus.bottleneck.label}` : 'No active backlog'}</strong>
              {stageFocus.bottleneck ? ' bottleneck signal' : ''}
            </span>
          </div>
          <button type="button" className="audit-pipeline-insights__clear-focus" onClick={onClearStage} title="Clear stage focus" aria-label="Clear stage focus">
            <X size={15} />
          </button>
        </div>
      )}

      <div className="audit-pipeline-insights__grid">
        {insights.map((process) => {
          const selectedIndex = selected.process === process.key ? selected.statusIndex : null;
          const chartOptions = {
            chart: {
              type: 'bar', toolbar: { show: false }, animations: { enabled: !loading, easing: 'easeout', speed: 420 },
              events: { dataPointSelection: (_event, _context, config) => selectStatus(process, config.dataPointIndex) },
            },
            plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '62%', distributed: true } },
            colors: process.statuses.map((_status, index) => index === selectedIndex ? process.color : `${process.color}aa`),
            dataLabels: { enabled: true, textAnchor: 'start', offsetX: 8, style: { colors: ['#334e53'], fontSize: '10px', fontWeight: 700 } },
            xaxis: { categories: process.statuses.map((status) => status.label), min: 0, tickAmount: 3, labels: { style: { colors: '#809096', fontSize: '10px' } } },
            yaxis: { labels: { style: { colors: '#52676d', fontSize: '10px', fontWeight: 600 }, maxWidth: 124 } },
            grid: { borderColor: '#e9eff0', strokeDashArray: 3, padding: { left: 5, right: 14, top: -10, bottom: -4 } },
            tooltip: { theme: 'light', y: { formatter: (value) => `${value} ${process.key === 'SCHEDULING' ? 'schedule entries' : 'tasks'}` } },
            legend: { show: false },
          };

          return (
            <article
              className={`audit-pipeline-insights__card${selectedStage === process.stageKey ? ' is-focus-stage' : ''}${selectedStage ? ' is-cross-filtered' : ''}`}
              key={process.key}
              style={{ '--process-color': process.color }}
            >
              <div className="audit-pipeline-insights__card-head">
                <div>
                  <h5>{process.label}</h5>
                  <span>{loading ? 'Loading...' : `${process.total} ${process.key === 'SCHEDULING' ? 'schedule entries' : 'tasks'}`}</span>
                </div>
                <span className="audit-pipeline-insights__accent" aria-hidden="true" />
              </div>

              <div className="audit-pipeline-insights__stage-metric">
                <strong>{loading ? '-' : process.currentStageProjects}</strong>
                <span>current-stage projects</span>
              </div>

              <div className={`audit-pipeline-insights__chart ${loading ? 'is-loading' : ''}`}>
                <ReactApexChart
                  options={chartOptions}
                  series={[{ name: process.label, data: process.statuses.map((status) => status.value) }]}
                  type="bar"
                  height={Math.max(156, process.statuses.length * 34 + 28)}
                />
              </div>

              <div className="audit-pipeline-insights__assignees">
                <span><UsersRound size={13} /> Assigned</span>
                {process.assigneeWorkload.length === 0
                  ? <em>No task assignee</em>
                  : process.assigneeWorkload.slice(0, 4).map(({ name, taskCount }) => <button type="button" className="audit-pipeline-insights__assignee" key={name} onClick={() => navigate?.(buildAssigneeAuditTaskHref(name))} title={`Open tasks assigned to ${name}`}>{name} <strong>({taskCount})</strong></button>)}
                {process.assigneeWorkload.length > 4 && <button type="button" className="audit-pipeline-insights__assignee-more" onClick={() => setAssigneeDialog(process)} aria-label={`View ${process.assigneeWorkload.length - 4} more ${process.label} assignees`}>+{process.assigneeWorkload.length - 4}</button>}
              </div>
            </article>
          );
        })}
      </div>

      {assigneeDialog && <div className="audit-assignee-modal__backdrop" role="presentation" onMouseDown={() => setAssigneeDialog(null)}><section className="audit-assignee-modal" role="dialog" aria-modal="true" aria-labelledby="audit-assignee-modal-title" onMouseDown={(event) => event.stopPropagation()}><header><div><span>Assigned workload</span><h5 id="audit-assignee-modal-title">{assigneeDialog.label}</h5></div><button type="button" onClick={() => setAssigneeDialog(null)} aria-label="Close assigned workload"><X size={17} /></button></header><div className="audit-assignee-modal__summary"><strong>{assigneeDialog.assigneeWorkload.length}</strong> assigned people <span>·</span> <strong>{assigneeDialog.assigneeWorkload.reduce((sum, item) => sum + item.taskCount, 0)}</strong> tasks</div><ol>{assigneeDialog.assigneeWorkload.map(({ name, taskCount }, index) => <li key={name}><span className="audit-assignee-modal__rank">{index + 1}</span><button type="button" className="audit-assignee-modal__name" onClick={() => navigate?.(buildAssigneeAuditTaskHref(name))}>{name}</button><strong>{taskCount} task{taskCount === 1 ? '' : 's'}</strong></li>)}</ol></section></div>}

      {showManagementAnalytics && (
        <section className="audit-management-analytics" aria-label="Management portfolio analytics">
          <div className="audit-management-analytics__heading">
            <div>
              <span>Portfolio intelligence</span>
              <h5>Workload composition & risk signals</h5>
            </div>
            <div className="audit-management-analytics__heading-meta">
              <small>Click any signal or chart bar to cross-filter the complete live process view.</small>
              {portfolioFilter && (
                <button type="button" onClick={() => onPortfolioFilterChange?.(null)} aria-label="Clear portfolio cross-filter">
                  {portfolioFilter.value} <X size={12} />
                </button>
              )}
            </div>
          </div>
          <div className="audit-management-analytics__signals">
            {[
              { label: 'Active workload', value: managementAnalytics.signals.activeTasks, filterValue: 'active', icon: Sparkles, tone: '#1f7a83' },
              { label: 'Completed', value: managementAnalytics.signals.completedTasks, filterValue: 'completed', icon: CheckCircle2, tone: '#16845b' },
              { label: 'Unassigned', value: managementAnalytics.signals.unassignedTasks, filterValue: 'unassigned', icon: UserMinus, tone: '#d97706' },
              { label: 'Stale workload', value: managementAnalytics.signals.staleTasks, filterValue: 'stale', icon: AlertTriangle, tone: '#c2415d' },
              { label: 'Active owners', value: managementAnalytics.signals.ownerCount, filterValue: 'assigned', icon: UsersRound, tone: '#6953b8' },
            ].map(({ label, value, filterValue, icon: Icon, tone }) => (
              <button
                type="button"
                key={label}
                className={portfolioFilter?.type === 'risk' && portfolioFilter?.value === filterValue ? 'is-selected' : ''}
                style={{ '--signal-tone': tone }}
                onClick={() => togglePortfolioFilter({ type: 'risk', value: filterValue })}
                aria-pressed={portfolioFilter?.type === 'risk' && portfolioFilter?.value === filterValue}
              >
                <Icon size={15} />
                <strong>{loading ? '—' : value}</strong>
                <span>{label}</span>
              </button>
            ))}
          </div>
          <div className="audit-management-analytics__charts">
            {[
              { title: 'By standard', type: 'standard', data: managementAnalytics.byStandard },
              { title: 'By program category', type: 'program', data: managementAnalytics.byService },
            ].map(({ title, type, data }) => {
              const topData = data.slice(0, 6);
              const activeLabel = portfolioFilter?.type === type ? portfolioFilter.value : null;
              return (
                <article className="audit-management-analytics__chart-card" key={title}>
                  <div className="audit-management-analytics__chart-title">
                    <h6>{title}</h6>
                    <span>{data.reduce((sum, item) => sum + item.value, 0)} project signals</span>
                  </div>
                  {topData.length === 0 ? (
                    <div className="audit-management-analytics__empty">No portfolio data</div>
                  ) : (
                    <ReactApexChart
                      type="donut"
                      height={Math.max(190, topData.length * 8 + 190)}
                      series={topData.map((item) => item.value)}
                      options={{
                        chart: {
                          type: 'donut',
                          animations: { enabled: !loading, speed: 420 },
                          events: {
                            dataPointSelection: (_event, _context, config) => {
                              const item = topData[config.dataPointIndex];
                              if (item) togglePortfolioFilter({ type, value: item.label });
                            },
                          },
                        },
                        labels: topData.map((item) => item.label),
                        colors: topData.map((item, index) => (
                          !activeLabel || activeLabel === item.label
                            ? DONUT_PALETTE[index % DONUT_PALETTE.length]
                            : `${DONUT_PALETTE[index % DONUT_PALETTE.length]}55`
                        )),
                        stroke: { width: 2, colors: ['#fff'] },
                        dataLabels: {
                          enabled: true,
                          formatter: (_value, opts) => `${opts.w.config.series[opts.seriesIndex]}`,
                          style: { fontSize: '11px', fontWeight: 700 },
                          dropShadow: { enabled: false },
                        },
                        legend: {
                          position: 'bottom', fontSize: '10px', labels: { colors: '#405b61' },
                          markers: { width: 8, height: 8 },
                          itemMargin: { horizontal: 6, vertical: 2 },
                        },
                        plotOptions: { pie: { donut: { size: '58%', labels: { show: true, total: { show: true, label: 'Total', fontSize: '11px' } } } } },
                        tooltip: { y: { formatter: (value) => `${value} project${value === 1 ? '' : 's'}` } },
                      }}
                    />
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default AuditPipelineInsights;
